// Home strip (BL-132): the mockup «Home», tweak `trial` - one line under the header, only in the states that need the
// user (ADR-0045: trial-ending, expired, past-due, paused; a gift in its last 10 days, BL-185); nothing in trial, active, cancelled, comp, off. No e-mail
// carries this (ADR-0047), so the strip and Account → Plan say it all.
import { Link } from "react-router";
import type { BillingPlan, BillingState } from "../api";
import { showsStrip, daysText, planPrices, subscriptionEnded } from "../billing";
import { t } from "../i18n";
import { useSession } from "../session";
import { keepsGiftDays, keepsTrialDays, longDay } from "./SubscribePage";
import { UpdateCardButton } from "./PlanCard";

function texts(plan: BillingPlan): { title: string; body: string; card: boolean } {
  const date = plan.ends_on ? longDay(plan.ends_on) : "";
  switch (plan.state) {
    case "trial-ending":
      return {
        title: t("strip.ending.title", { left: daysText(plan.days_left ?? 0) }),
        body: t(keepsTrialDays(plan) ? "strip.ending.body" : "strip.ending.bodyShort", { date }),
        card: false,
      };
    case "gift":
      return {
        title: t("strip.giftEnding.title", { left: daysText(plan.days_left ?? 0) }),
        body: t(keepsGiftDays(plan) ? "strip.giftEnding.body" : "strip.ending.bodyShort", { date }),
        card: false,
      };
    case "expired":
      return { title: t(subscriptionEnded(plan) ? "strip.ended.title" : "strip.expired.title", { date }), body: t("strip.expired.body"), card: false };
    case "past-due":
      return {
        title: t("strip.pastDue.title"),
        body: plan.pause_on ? t("strip.pastDue.body", { date: longDay(plan.pause_on) }) : t("strip.pastDue.bodyNoDate"),
        card: true,
      };
    default:
      return { title: t("strip.paused.title"), body: t("strip.paused.body"), card: true };
  }
}

/** `state` is the one the Home page loads for the strip and the Health status together. */
export function PlanStrip({ state }: { state: BillingState | null | undefined }) {
  const { me } = useSession();
  if (!me || !state || !showsStrip(state.plan)) return null;
  const { title, body, card } = texts(state.plan);
  return (
    <div className="plan-strip" role="status" data-testid="plan-strip" data-state={state.plan.state}>
      <span className="plan-strip-dot" aria-hidden="true" />
      <span className="plan-strip-text">
        <strong>{title}</strong> {body}
      </span>
      <span className="plan-strip-end">
        {/* The price line is for those choosing to subscribe; a subscriber with a failed charge needs only the card. */}
        {!card && (
          <Link className="plan-strip-price" to="/pricing">
            {t("strip.price", { price: planPrices(state.plan).monthly })}
          </Link>
        )}
        {card ? (
          <UpdateCardButton label={t("strip.updateCard")} className="btn btn-primary plan-strip-cta" />
        ) : (
          <Link className="btn btn-primary plan-strip-cta" to="/subscribe">
            {t("strip.subscribe")}
          </Link>
        )}
      </span>
    </div>
  );
}
